import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../../../../stores/userStore';
import { saveCurrentPathAndRedirectToLogin } from '../../../../utils/authUtils';
import { ratingDistribution } from './memberReviewListModel';
import ProfileIdentity from '../../../../components/content/ProfileIdentity';
import baseline, { appTextStyle } from '../../../../app-ui/baseline';
import AlbumActionEditor from '../album/AlbumActionEditor';
import AlbumActionConfirm from '../album/AlbumActionConfirm';
import { saveAlbumReview, deleteAlbumReview } from '../album/albumActionModel';
import AlbumMyReview from '../my-review/AlbumMyReview';
import { recentScope, removeRecent } from '../../../../features/recent/recentContentStore';
import { useReviewDraft } from '../detail/ReviewDraftProtection';
import explicitIcon from '../../../../assets/explicit_icon.svg';

export function MemberAlbumSummary({ album, to, en }) {
  return (
    <Link
      className="pp-member-album-summary"
      to={to}
      aria-label={en ? `Open ${album.title}` : `${album.title} 앨범으로`}
    >
      <span className="pp-member-album-art">
        postpop
        {album.imageUrl && (
          <img
            src={album.imageUrl}
            alt=""
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
          />
        )}
      </span>
      <span className="pp-member-album-copy">
        <strong style={appTextStyle(baseline.contentText.title)}>
          {album.title}
          {album.contentRating === 'EXPLICIT' && (
            <img className="pp-member-explicit" src={explicitIcon} alt="Explicit" />
          )}
        </strong>
        <span style={appTextStyle(baseline.contentText.artist)}>
          {album.artist?.name || (en ? 'Unknown artist' : '아티스트 정보 없음')}
        </span>
        <span className="pp-member-album-meta" style={appTextStyle(baseline.contentText.metadata)}>
          {[
            album.releaseDate?.slice(0, 10).replaceAll('-', '.'),
            (album.genres ?? []).filter(Boolean).slice(0, 3).join(', '),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
    </Link>
  );
}
export function MemberDistribution({ records, average, reviewCount, en }) {
  const { counts, percentages } = useMemo(() => ratingDistribution(records), [records]);
  const key = counts.join(':');
  const [progress, setProgress] = useState(1);
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let frame;
    const start = () => {
      cancelAnimationFrame(frame);
      if (media?.matches) {
        setProgress(1);
        return;
      }
      setProgress(0);
      const at = performance.now();
      const tick = (now) => {
        const next = Math.min(1, (now - at) / 900);
        setProgress(next);
        if (next < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };
    start();
    media?.addEventListener?.('change', start);
    return () => {
      cancelAnimationFrame(frame);
      media?.removeEventListener?.('change', start);
    };
  }, [key, average]);
  const windowValue = (start, end) => Math.max(0, Math.min(1, (progress - start) / (end - start)));
  const sampled = (start, end) => Math.round(windowValue(start, end) * 12) / 12;
  const labels = en ? ['Low', 'Average', 'High'] : ['낮은 점수', '보통', '높은 점수'];
  return (
    <section
      className="pp-member-distribution"
      aria-label={
        en
          ? `Average ${average.toFixed(1)}, ${records.length} ratings, ${reviewCount} reviews`
          : `평균 ${average.toFixed(1)}, 전체 ${records.length}명, 리뷰 ${reviewCount}명`
      }
    >
      <div className="pp-member-distribution-summary">
        <strong aria-hidden="true">{(average * sampled(0.04, 0.42)).toFixed(1)}</strong>
        <span>
          {en
            ? `${records.length} total · ${reviewCount} reviews`
            : `전체 ${records.length}명 · 리뷰 ${reviewCount}명`}
        </span>
      </div>
      <div className="pp-member-distribution-chart">
        <div className="pp-member-distribution-band">
          <div style={{ transform: `scaleX(${windowValue(0.18, 0.9)})` }}>
            {counts.map((count, i) => (
              <span
                key={i}
                style={{ flexGrow: count, opacity: [0.46, 0.72, 1][i] }}
                aria-label={`${labels[i]} ${count} · ${percentages[i]}%`}
              />
            ))}
          </div>
        </div>
        <div className="pp-member-distribution-labels">
          {labels.map((label, i) => (
            <div key={label}>
              <span>{label}</span>
              <strong aria-label={`${percentages[i]}%`}>
                {Math.round(
                  percentages[i] * sampled(0.3 + i * 0.08, Math.min(0.94, 0.68 + i * 0.1)),
                )}
                %
              </strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export function RatingMember({ review, en }) {
  const user = review.user ?? {};
  const name = user.nickname || '';
  return (
    <Link
      className="pp-member-rating-card"
      to={`/users/${encodeURIComponent(name)}`}
      aria-label={`${name} · ${Number(review.rating).toFixed(1)} · ${review.createdAt?.slice(0, 10) || ''}`}
    >
      <span className="pp-member-rating-avatar">
        {name.slice(0, 1)}
        {user.profileImageUrl && (
          <img
            src={user.profileImageUrl}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
          />
        )}
      </span>
      <ProfileIdentity name={name} mark={user.profileMark} avatar={false} />
      <span
        className="pp-member-rating-value"
        aria-label={`${en ? 'Rating' : '평점'} ${Number(review.rating).toFixed(1)}`}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            fill="#f16438"
            d="M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z"
          />
        </svg>
        {Number(review.rating).toFixed(1)}
      </span>
      <span className="pp-member-rating-date">
        {review.createdAt?.slice(0, 10).replaceAll('-', '.')}
      </span>
    </Link>
  );
}
export function OwnMemberReview({ album, record, en, user }) {
  const navigate = useNavigate(),
    client = useQueryClient();
  const [editing, setEditing] = useState(false),
    [confirm, setConfirm] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false),
    [editorState, setEditorState] = useState({ dirty: false, busy: false });
  useReviewDraft(editing && editorState.dirty, busy || (editing && editorState.busy), en);
  const active = useRef(true),
    pending = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const assertAccount = () => {
    if (!active.current || !user || String(useUserStore.getState().user?.id) !== String(user.id))
      throw new Error('Account changed');
  };
  const refresh = () => {
    for (const key of [
      'albumDetail',
      'albumReviews',
      'review',
      'reviewsAlbums',
      'browse-reviews',
      'me',
      'myReviews',
      'album-review-history',
    ])
      void client.invalidateQueries({ queryKey: [key] });
  };
  const compose = () => (user ? setEditing(true) : saveCurrentPathAndRedirectToLogin(navigate));
  const save = async (draft) => {
    assertAccount();
    await saveAlbumReview(album, record, draft, 'review');
    assertAccount();
    refresh();
  };
  const remove = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(false);
    try {
      assertAccount();
      await deleteAlbumReview(album, record);
      assertAccount();
      removeRecent(
        recentScope(user.id),
        (item) => item.kind === 'review' && item.id === `review:${record.id}`,
      );
      refresh();
      setConfirm(false);
    } catch {
      if (active.current) setError(true);
    } finally {
      pending.current = false;
      if (active.current) setBusy(false);
    }
  };
  return (
    <>
      <AlbumMyReview
        album={{ ...album, myReview: record, userRating: record?.rating ?? 0 }}
        reviews={record ? [record] : []}
        onCompose={compose}
        onRemove={() => setConfirm(true)}
        deleting={busy}
      />
      {editing && (
        <AlbumActionEditor
          album={{ ...album, userRating: record?.rating ?? 0, myReview: record }}
          review={record}
          mode="review"
          en={en}
          onClose={() => {
            setEditing(false);
            setEditorState({ dirty: false, busy: false });
          }}
          onStateChange={setEditorState}
          onSave={save}
          onDelete={remove}
        />
      )}
      {confirm && (
        <AlbumActionConfirm
          kind="review"
          en={en}
          hasReview
          busy={busy}
          error={error}
          onCancel={() => {
            if (!pending.current) setConfirm(false);
          }}
          onConfirm={remove}
        />
      )}
    </>
  );
}
